// Rotas do módulo fiscal (react-router-dom v6).
// No App.js do seu sistema:
//   <Route path="/fiscal/*" element={<FiscalRoutes />} />
import React, { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { fiscal } from "./api";
import { Aviso } from "./components/ui";
import AdminCertificados from "./pages/AdminCertificados";
import AdminIntegracao from "./pages/AdminIntegracao";
import Configuracao from "./pages/Configuracao";
import { AvisoDocumentoBloqueado, useDocumentos } from "./components/DocumentoLiberado";
import NFeForm from "./pages/NFeForm";
import NFSeForm from "./pages/NFSeForm";
import Recorrentes from "./pages/Recorrentes";
import NotasLista from "./pages/NotasLista";
import Relatorios from "./pages/Relatorios";
import Sefaz from "./pages/Sefaz";

// Todos os links usam caminho ABSOLUTO (base + aba). Links relativos dentro de rota "/*"
// mudam de comportamento entre versões do react-router (v7_relativeSplatPath) e
// acabavam empilhando (ex.: /fiscal/configuracoes/nfe/nova).
const BaseCtx = createContext("");

export function baseFiscal(pathname) {
  const m = pathname.match(/^(.*?\/fiscal)(?=\/|$)/);
  return m ? m[1] : "";
}

function useIr() {
  const base = useContext(BaseCtx);
  const navigate = useNavigate();
  return (sub) => navigate(`${base}/${sub}`);
}

// 3º item: tipo de nota que precisa estar liberado (Admin Master → Fiscal → Documentos) para o atalho aparecer
const MENU = [
  ["notas", "Notas"],
  ["nfe/nova", "Nova NF-e", "nfe"],
  ["nfse/nova", "Nova NFS-e", "nfse"],
  ["nfse/recorrentes", "NFS-e recorrentes", "nfse"],
  ["sefaz", "SEFAZ"],
  ["relatorios", "Relatórios e exportação"],
  ["configuracoes", "Configurações"],
];

function useEmissor() {
  const [emissor, setEmissor] = useState(undefined);
  const [bloqueio, setBloqueio] = useState(null); // módulo não contratado
  useEffect(() => {
    fiscal.emissor().then(setEmissor).catch((e) => {
      if (e.status === 403) setBloqueio(e.message);
      setEmissor(null);
    });
  }, []);
  return [emissor, bloqueio];
}

function NovaNFe({ emissor }) {
  const ir = useIr();
  if (emissor === undefined) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (!emissor) return <Aviso tipo="atencao">Configure o emissor em Configurações antes de emitir.</Aviso>;
  return <><AvisoDocumentoBloqueado tipo="nfe" /><NFeForm emissor={emissor} onEmitida={() => setTimeout(() => ir("notas"), 2500)} /></>;
}

function CorrigirNFe({ emissor }) {
  const { id } = useParams();
  const ir = useIr();
  const [nota, setNota] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    fiscal.nota(id).then((n) => setNota({ ...n, itens: n.itens_digitados || n.itens })).catch((e) => setErro(e.message));
  }, [id]);
  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!nota || !emissor) return <p className="text-sm text-gray-500">Carregando…</p>;
  return <><AvisoDocumentoBloqueado tipo="nfe" /><NFeForm emissor={emissor} notaRejeitada={nota} onEmitida={() => setTimeout(() => ir("notas"), 2500)} /></>;
}

function NovaNFSe({ emissor }) {
  const ir = useIr();
  if (emissor === undefined) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (!emissor) return <Aviso tipo="atencao">Configure o emissor em Configurações antes de emitir.</Aviso>;
  return <><AvisoDocumentoBloqueado tipo="nfse" /><NFSeForm emissor={emissor} onEmitida={() => setTimeout(() => ir("notas"), 4000)} /></>;
}

function CorrigirNFSe({ emissor }) {
  const { id } = useParams();
  const ir = useIr();
  const [nota, setNota] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => { fiscal.nfseParaCorrecao(id).then(setNota).catch((e) => setErro(e.message)); }, [id]);
  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!nota || !emissor) return <p className="text-sm text-gray-500">Carregando…</p>;
  return <><AvisoDocumentoBloqueado tipo="nfse" /><NFSeForm emissor={emissor} notaRejeitada={nota} onEmitida={() => setTimeout(() => ir("notas"), 4000)} /></>;
}

function SubstituirNFSe({ emissor }) {
  const { id } = useParams();
  const ir = useIr();
  const [nota, setNota] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => { fiscal.nfseParaCorrecao(id).then(setNota).catch((e) => setErro(e.message)); }, [id]);
  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!nota || !emissor) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (nota.status !== "autorizada") return <Aviso tipo="atencao">Só NFS-e autorizada pode ser substituída.</Aviso>;
  return <><AvisoDocumentoBloqueado tipo="nfse" /><NFSeForm emissor={emissor} notaSubstituida={{ id: nota.id, numero: nota.numero, dados: nota }}
    onEmitida={() => setTimeout(() => ir("notas"), 4000)} /></>;
}

export default function FiscalRoutes({ isAdmin = false, basePath }) {
  const { pathname } = useLocation();
  const base = (basePath || baseFiscal(pathname)).replace(/\/$/, "");
  return (
    <BaseCtx.Provider value={base}>
      <FiscalInterno isAdmin={isAdmin} base={base} />
    </BaseCtx.Provider>
  );
}

function FiscalInterno({ isAdmin, base }) {
  const [emissor, bloqueio] = useEmissor();
  const ir = useIr();
  const docs = useDocumentos(); // hooks sempre antes de qualquer "return" (regra dos hooks do React)
  if (bloqueio && !isAdmin) {
    return <div className="mx-auto max-w-3xl p-4"><Aviso tipo="atencao">{bloqueio}</Aviso></div>;
  }
  // fail-closed: atalho de emissão só aparece quando o servidor confirma que o tipo está liberado
  const visiveis = MENU.filter(([, , tipo]) => !tipo || docs?.[tipo]?.liberado === true);
  const menu = isAdmin ? [...visiveis, ["admin/integracao", "Integração (admin)"], ["admin/certificados", "Certificados (admin)"]] : visiveis;

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4">
      <nav className="flex flex-wrap gap-1 border-b pb-2 border-gray-200">
        {menu.map(([to, rotulo]) => (
          <NavLink key={to} to={`${base}/${to}`}
            className={({ isActive }) => `rounded-md px-3 py-1.5 text-sm ${isActive ? "bg-blue-50 font-medium text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}>
            {rotulo}
          </NavLink>
        ))}
      </nav>
      <Routes>
        <Route index element={<NotasLista onCorrigir={(n) => ir(`${n.modelo === "nfse" ? "nfse" : "nfe"}/${n.id}/corrigir`)}
          onSubstituir={(n) => ir(`nfse/${n.id}/substituir`)} />} />
        <Route path="notas" element={<NotasLista onCorrigir={(n) => ir(`${n.modelo === "nfse" ? "nfse" : "nfe"}/${n.id}/corrigir`)}
          onSubstituir={(n) => ir(`nfse/${n.id}/substituir`)} />} />
        <Route path="nfe/nova" element={<NovaNFe emissor={emissor} />} />
        <Route path="nfe/:id/corrigir" element={<CorrigirNFe emissor={emissor} />} />
        <Route path="nfse/nova" element={<NovaNFSe emissor={emissor} />} />
        <Route path="nfse/:id/corrigir" element={<CorrigirNFSe emissor={emissor} />} />
        <Route path="nfse/:id/substituir" element={<SubstituirNFSe emissor={emissor} />} />
        <Route path="nfse/recorrentes" element={emissor ? <Recorrentes emissor={emissor} /> : <p className="text-sm text-gray-500">Carregando…</p>} />
        <Route path="sefaz" element={<Sefaz />} />
        <Route path="relatorios" element={<Relatorios />} />
        <Route path="configuracoes" element={<Configuracao />} />
        {isAdmin && <Route path="admin/integracao" element={<AdminIntegracao />} />}
        {isAdmin && <Route path="admin/certificados" element={<AdminCertificados />} />}
      </Routes>
    </div>
  );
}
